/**
 * Section B7.2 Service - B.7.2.專案計算
 * Calculation of project emissions/removals
 *
 * 架構說明：每筆 EquationEntry 即一個完整的方程式資料單元，
 * 包含 6 個階段資料，以 updateEquation 單一端點儲存。
 */
import axios from '../api/axios';

const BASE_URL = '/api/section-b7-2';

// ── equations JSON 子結構 ─────────────────────────────────────────

export interface MethodChoiceItem {
  symbol: string;
  para_type?: number | null;   // 249=事前固定, 250=事後監測, null=皆適用
  scenarios: string;            // 純文字
  unit: string;                 // LaTeX 語法
  annual_value?: number | null; // 保守評估階段填寫 (10,2)
}

export interface CalculationResult {
  value: number | null;
  unit: string;
}

export interface EquationEntry {
  id: string;                             // UUID v4
  // Stage 1: 計算公式
  methodology: string;
  equation: string;                       // LaTeX 公式
  equation_note: string;                  // 富文本，ex ante/ex post 差異說明
  // Stage 2: 方法論說明
  method_choices: MethodChoiceItem[];
  // Stage 3: 設備效能說明
  is_performance: boolean;
  performance_equipment: string;
  // Stage 4: 量測檢定標準與程序
  is_procedures: boolean;
  standards_procedures: string;
  // Stage 5: 保守評估說明
  calculation_result: CalculationResult;
  conservative_description: string;
  // Stage 6: 抽樣計畫說明
  is_sampling: boolean;
  sampling_plan: string;
}

// ── 主資料表 ──────────────────────────────────────────────────────

export interface SectionB72Detail {
  id: number;
  organization_id: number;
  project_id: number;
  equations: EquationEntry[] | null;
  b7_2: number;
  edit_by: number;
  created_at: string;
  updated_at: string | null;
}

// ── Request Payloads ──────────────────────────────────────────────

export interface EquationEntryCreate {
  methodology: string;
  equation: string;
  equation_note: string;
}

export interface EquationEntryUpdate {
  methodology?: string;
  equation?: string;
  equation_note?: string;
  method_choices?: MethodChoiceItem[];
  is_performance?: boolean;
  performance_equipment?: string | null;
  is_procedures?: boolean;
  standards_procedures?: string | null;
  calculation_result?: CalculationResult;
  conservative_description?: string | null;
  is_sampling?: boolean;
  sampling_plan?: string | null;
}

// ── API 函式 ──────────────────────────────────────────────────────

export const getSectionB72ByProject = async (projectId: number): Promise<SectionB72Detail | null> => {
  const response = await axios.get<SectionB72Detail>(`${BASE_URL}/by-project/${projectId}`);
  return response.data;
};

export const addEquation = async (projectId: number, entry: EquationEntryCreate): Promise<SectionB72Detail> => {
  const response = await axios.post<SectionB72Detail>(`${BASE_URL}/${projectId}/equations`, entry);
  return response.data;
};

export const updateEquation = async (projectId: number, entryId: string, entry: EquationEntryUpdate): Promise<SectionB72Detail> => {
  const response = await axios.put<SectionB72Detail>(`${BASE_URL}/${projectId}/equations/${entryId}`, entry);
  return response.data;
};

export const deleteEquation = async (projectId: number, entryId: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/${projectId}/equations/${entryId}`);
};

export const resetSectionB72 = async (projectId: number): Promise<void> => {
  await axios.post(`${BASE_URL}/${projectId}/reset`);
};

const sectionB72Service = {
  getSectionB72ByProject,
  addEquation,
  updateEquation,
  deleteEquation,
  resetSectionB72,
};
export default sectionB72Service;
